import { useEffect, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'

export interface UseStudioEngineResult {
  scene: BABYLON.Scene | null
  engine: BABYLON.Engine | null
}

/**
 * Owns the Babylon `Engine`/`Scene` lifecycle for one `<canvas>`. Every other
 * studio hook takes the `scene` this returns and is a no-op until it's
 * non-null, so composing them is just "call them all with the same scene".
 *
 * Ported 1:1 from the prototype's boot sequence (engine + scene + fog +
 * ACES tonemap look), minus the pipeline/cameras — those belong to
 * `useStudioCameraRig` since they're camera-specific.
 */
/**
 * `enabled` gates *when* the engine boots — the canvas element itself must
 * already be mounted (unconditionally, even behind a loading overlay) so
 * `canvasRef.current` is set the instant this flips true. Used to wait for
 * `useStudio3DData` to resolve a real seed before building the scene, so
 * `useStudioRoom`/`useStudioArts` (which seed once per `scene` instance)
 * never race an empty/default seed.
 */
export function useStudioEngine(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  enabled = true,
): UseStudioEngineResult {
  const [scene, setScene] = useState<BABYLON.Scene | null>(null)
  const engineRef = useRef<BABYLON.Engine | null>(null)

  useEffect(() => {
    if (!enabled) return
    const canvas = canvasRef.current
    if (!canvas) return

    const engine = new BABYLON.Engine(canvas, true, { antialias: true, adaptToDeviceRatio: true })
    const nextScene = new BABYLON.Scene(engine)
    nextScene.clearColor = BABYLON.Color4.FromHexString('#0b0a10ff')
    nextScene.fogMode = BABYLON.Scene.FOGMODE_EXP2
    nextScene.fogDensity = 0.008
    nextScene.fogColor = BABYLON.Color3.FromHexString('#120e1b')

    engineRef.current = engine
    setScene(nextScene)

    const onResize = () => engine.resize()
    window.addEventListener('resize', onResize)

    // The camera rig attaches its cameras in a separate effect that runs
    // slightly after this one — guard against the couple of frames in
    // between where the scene has no activeCamera yet.
    engine.runRenderLoop(() => {
      if (!nextScene.activeCamera) return
      nextScene.render()
    })

    return () => {
      window.removeEventListener('resize', onResize)
      nextScene.dispose()
      engine.dispose()
      engineRef.current = null
      setScene(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  return { scene, engine: engineRef.current }
}
